import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import { uniTestkitFactoryCreator } from '@wix/wix-ui-test-utils/vanilla';
import { DatePickerInput, DatePickerInputProps } from '..';
import { datePickerInputOldDriverFactory } from './DatePickerInputOld.driver';
import { classes } from './DatePickerInputOld.visual.st.css';
import { ErrorAppearance } from '../internal/ErrorMessageWrapper/types';
import { EditorShell } from '../../test/visual/VisualAsync';

const createDriver = uniTestkitFactoryCreator(datePickerInputOldDriverFactory);
const dataHook = 'storybook-datePickerInput';

interface DatePickerInputVisualProps {
  datePickerInputProps: DatePickerInputProps;
  onDone(): void;
}

class DatePickerInputVisualComponent extends React.Component<DatePickerInputVisualProps> {
  static defaultProps = {
    datePickerInputProps: {},
    onDone: () => {},
  };

  async componentDidMount() {
    const { onDone } = this.props;
    const driver = createDriver({ wrapper: document.body, dataHook });

    await driver.openCalendarWindow();

    onDone();
  }
  render() {
    const { datePickerInputProps } = this.props;

    return (
      <EditorShell>
        <DatePickerInput data-hook={dataHook} {...datePickerInputProps} />
      </EditorShell>
    );
  }
}

visualize('DatePickerInputOld', () => {
  story('simple example', () => {
    snap('default props', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap(
      'Without a selected date',
      <DatePickerInput
        value={null}
        onChange={() => {}}
        placeholderText="Select A Date"
      />,
    );
    snap('FilterDate - Prior for today dates (includes today)', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          filterDate: (date) => date <= new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('FilterDate - feature dates only (includes today)', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          filterDate: (date) => date >= new Date('2020/10/10'),
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Months dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showMonthDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Years dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showYearDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap('Months and years dropdown', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          showMonthDropdown: true,
          showYearDropdown: true,
          onChange: () => {},
        }}
        onDone={done}
      />
    ));
    snap(
      'Disabled Mode',
      <DatePickerInput
        value={new Date('2020/09/20')}
        onChange={() => {}}
        placeholderText="Select A Date"
        disabled
      />,
    );
    snap(
      'Error mode',
      <DatePickerInput
        value={new Date('2020/09/20')}
        onChange={() => {}}
        placeholderText="Select A Date"
        hasError
        errorMessage="Error Message"
      />,
    );
    snap(
      'New error mode',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with message',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with message / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles and message',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap(
      'New error mode with override styles and message / ErrorAppearance - BackgroundAndBorder',
      <div>
        <DatePickerInput
          value={new Date('2020/09/20')}
          onChange={() => {}}
          placeholderText="Select A Date"
          hasError
          errorMessage="Error Message"
          newErrorMessage
          errorAppearance={ErrorAppearance.BackgroundAndBorder}
          className={classes.root}
        />
        <div className={classes.elementBelow}>
          An element to show the spacing
        </div>
      </div>,
    );
    snap('Open with new error message', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          placeholderText: 'Select A Date',
          onChange: () => {},
          hasError: true,
          errorMessage: 'Error Message',
          newErrorMessage: true,
        }}
        onDone={done}
      />
    ));
    snap(
      'Open with new error message / ErrorAppearance - BackgroundAndBorder',
      (done) => (
        <DatePickerInputVisualComponent
          datePickerInputProps={{
            value: new Date('2020/10/10'),
            placeholderText: 'Select A Date',
            onChange: () => {},
            hasError: true,
            errorMessage: 'Error Message',
            errorAppearance: ErrorAppearance.BackgroundAndBorder,
            newErrorMessage: true,
          }}
          onDone={done}
        />
      ),
    );
    snap('With label', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/10/10'),
          onChange: () => {},
          label: 'Label',
        }}
        onDone={done}
      />
    ));
    snap('With custom border', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/09/20'),
          onChange: () => {},
          className: classes.rootWithCustomBorder,
        }}
        onDone={done}
      />
    ));
    snap('With design overrides', (done) => (
      <DatePickerInputVisualComponent
        datePickerInputProps={{
          value: new Date('2020/09/20'),
          onChange: () => {},
          className: classes.rootWithDesignOverrides,
        }}
        onDone={done}
      />
    ));
  });
});
